<script lang="ts" setup>
/**
 * Router link to a public, locale-prefixed page.
 *
 * Public routes live under `/:locale(de|en)`, so `router.resolve({ name })`
 * THROWS `Missing required param "locale"` whenever it is called from a
 * location that has no locale param of its own. Every dashboard, cart and
 * webmail page is such a location, and `<router-link>` resolves inside a
 * render-time computed, so the throw blanks the enclosing subtree with no
 * console error. That is how the legal links in the dashboard nav and the
 * webmail footer would have disappeared.
 *
 * The locale comes from `useI18n()`, deliberately not from the module-scope
 * accessor in @/i18n: that one is client-only by contract and returns null
 * during SSR, which is exactly the crawler-facing path.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'

const props = defineProps<{
  /** Named public route, e.g. 'agb'. */
  to: string
  /** Extra params for routes that take them. */
  params?: Record<string, string>
}>()

const { locale } = useI18n()

const target = computed<RouteLocationRaw>(() => ({
  name: props.to,
  params: { locale: locale.value, ...(props.params ?? {}) }
}))
</script>

<template>
  <router-link :to="target">
    <slot />
  </router-link>
</template>
